import { FollowWithUser } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { Button, EmptyState, LoadingSpinner, UserCard } from '@/src/presentation/components';
import { Ionicons } from '@expo/vector-icons';
import { FlatList, RefreshControl, StyleSheet, Text, View } from 'react-native';

export interface FollowingScreenProps {
  following: FollowWithUser[];
  loading: boolean;
  loadingMore: boolean;
  currentUserId?: string;
  followingIds: Set<string>;
  onFollowToggle: (userId: string) => void;
  onUserPress: (userId: string) => void;
  onRefresh: () => void;
  onLoadMore: () => void;
  colors: {
    background: string;
    surface: string;
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
  };
}

export const FollowingScreen = ({
  following,
  loading,
  loadingMore,
  currentUserId,
  followingIds,
  onFollowToggle,
  onUserPress,
  onRefresh,
  onLoadMore,
  colors,
}: FollowingScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          <Text style={[styles.title, { color: colors.text }]}>{t('navigation.following')}</Text>
        </View>
        <LoadingSpinner />
      </View>
    );
  }

  const renderFollowingItem = ({ item }: { item: FollowWithUser }) => {
    const isCurrentUser = item.followingId === currentUserId;
    const isFollowing = followingIds.has(item.followingId);

    return (
      <UserCard
        userId={item.followingId}
        userName={item.userName}
        userEmail={item.userEmail}
        userAvatarUrl={item.userAvatarUrl}
        onPress={() => onUserPress(item.followingId)}
        actions={
          !isCurrentUser ? (
            <Button
              title={isFollowing ? t('social.unfollow') : t('social.follow')}
              onPress={() => onFollowToggle(item.followingId)}
              variant={isFollowing ? 'secondary' : 'primary'}
              size="small"
              colors={colors}
            />
          ) : null
        }
        colors={colors}
      />
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <Text style={[styles.title, { color: colors.text }]}>{t('navigation.following')}</Text>
        <Text style={[styles.count, { color: colors.textSecondary }]}>
          {following.length} {t('followers.following')}
        </Text>
      </View>

      <FlatList
        data={following}
        renderItem={renderFollowingItem}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.list}
        onEndReached={onLoadMore}
        onEndReachedThreshold={0.5}
        refreshControl={
          <RefreshControl refreshing={loading} onRefresh={onRefresh} tintColor={colors.primary} />
        }
        ListFooterComponent={loadingMore ? <LoadingSpinner /> : null}
        ListEmptyComponent={
          <EmptyState
            icon={<Ionicons name="person-add-outline" size={52} color={colors.textTertiary} />}
            title={t('social.notFollowingAnyone')}
            colors={colors}
          />
        }
      />
    </View>
  );
};

const createStyles = () =>
  StyleSheet.create({
    container: {
      flex: 1,
    },
    header: {
      paddingHorizontal: 20,
      paddingVertical: 16,
      borderBottomWidth: 1,
    },
    title: {
      fontSize: 24,
      fontWeight: 'bold',
      marginBottom: 4,
    },
    count: {
      fontSize: 14,
    },
    list: {
      padding: 16,
    },
  });
